const DEFAULT_TIMEOUT_MS = 30_000
const DEFAULT_MAX_RETRIES = 3
const RETRY_BASE_DELAY_MS = 500
const MAX_RETRY_DELAY_MS = 30_000
const RETRYABLE_STATUS_CODES = new Set([429, 502, 503, 504])

/** Canonical send-side casing of the active-ontology-branch header. */
export const ONTOLOGY_BRANCH_HEADER = 'X-Ontology-Branch'
/** Canonical send-side casing of the active-ontology-site header. */
export const ONTOLOGY_SITE_HEADER = 'X-Ontology-Site'
/** Header used by global administrators for read-only user previews. */
export const VIEW_AS_USER_HEADER = 'X-Axilon-View-As-User'
/** The proxy's largest list page; a read that fills it may have more rows. */
export const PROXY_MAX_PAGE_SIZE = 500

export interface ProxyHttpClientConfig {
  endpoint: string
  apiKey: string
  timeoutMs?: number
  authToken?: string
  maxRetries?: number
  /**
   * Active ontology branch. When set, every request from this client carries
   * it as the `X-Ontology-Branch` header unless a per-call `options.headers`
   * entry overrides it. Usually set via `withBranch` rather than directly.
   */
  branch?: string
  /**
   * Active ontology site. When set, every request from this client carries
   * it as the `X-Ontology-Site` header unless a per-call `options.headers`
   * entry overrides it. Usually set via `withSite` rather than directly.
   */
  site?: string
  organizationId?: string
  viewAsUserId?: string
}

export interface ProxyRequestOptions {
  body?: unknown
  headers?: Record<string, string>
  timeoutMs?: number
  /** Set to 0 to disable retries for this request. */
  maxRetries?: number
  /**
   * External abort signal — typically the agent loop's run-scoped signal
   * OR-ed with a per-tool-call timeout. When it fires, the in-flight
   * fetch is aborted and the request short-circuits the retry loop
   * (caller sees an `AbortError`-named error). An already-aborted signal
   * makes `request()` throw before sending.
   */
  signal?: AbortSignal
}

export interface ProxyHttpErrorDetails {
  code?: string
  reason?: string
  message?: string
}

type ProxyHttpErrorKind = 'failed' | 'auth failed'

function parseErrorDetails(body: string): ProxyHttpErrorDetails | undefined {
  try {
    const parsed = JSON.parse(body) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined
    const record = parsed as Record<string, unknown>
    return {
      ...(typeof record.code === 'string' ? { code: record.code } : {}),
      ...(typeof record.reason === 'string' ? { reason: record.reason } : {}),
      ...(typeof record.message === 'string' ? { message: record.message } : {})
    }
  } catch {
    return undefined
  }
}

export class ProxyHttpError extends Error {
  readonly method: string
  readonly path: string
  readonly status: number
  readonly body: string
  readonly code: string | undefined
  readonly reason: string | undefined
  readonly responseMessage: string | undefined

  constructor(
    method: string,
    path: string,
    status: number,
    body: string,
    kind: ProxyHttpErrorKind = 'failed'
  ) {
    super(`Proxy ${method} ${path} ${kind} (${status}): ${body}`)
    this.name = 'ProxyHttpError'
    this.method = method
    this.path = path
    this.status = status
    this.body = body
    const details = parseErrorDetails(body)
    this.code = details?.code
    this.reason = details?.reason
    this.responseMessage = details?.message
  }
}

/** No complete response was received; a mutation may still have succeeded. */
export class ProxyTransportError extends Error {
  constructor(
    readonly method: string,
    readonly path: string,
    detail: string
  ) {
    super(`Proxy ${method} ${path} failed: ${detail}`)
    this.name = 'ProxyTransportError'
  }
}

function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const name = (err as { name?: unknown }).name
  return name === 'AbortError'
}

function makeAbortError(message: string): Error {
  const err = new Error(message)
  err.name = 'AbortError'
  return err
}

/** Validate and normalize the endpoint, stripping trailing slashes for clean path concatenation. */
function normalizeEndpoint(rawUrl: string): string {
  try {
    const url = new URL(rawUrl)
    const pathname = url.pathname.replace(/\/+$/, '')
    return url.origin + pathname
  } catch {
    throw new Error(`Invalid proxy endpoint URL: ${rawUrl}`)
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function retryDelay(attempt: number): number {
  return Math.min(RETRY_BASE_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS)
}

function isBinaryBody(body: unknown): body is ArrayBuffer | Uint8Array {
  return (
    body instanceof ArrayBuffer ||
    body instanceof Uint8Array ||
    (typeof Buffer !== 'undefined' && Buffer.isBuffer(body))
  )
}

/**
 * JSON-encode object bodies; pass CSV/bytes through unchanged so dump PUTs
 * are not `JSON.stringify`'d into `{"type":"Buffer","data":[…]}`.
 */
function serializeRequestBody(
  body: unknown,
  headers: Record<string, string>
): string | Uint8Array | ArrayBuffer | undefined {
  if (body === undefined || body === null) return undefined
  if (isBinaryBody(body)) return body
  headers['Content-Type'] ??= 'application/json'
  return JSON.stringify(body)
}

/**
 * Parse the `Retry-After` header value.
 * Supports both delta-seconds (`120`) and HTTP-date (`Thu, 01 Dec 1994 16:00:00 GMT`).
 * Returns delay in milliseconds, or `undefined` if unparsable.
 */
function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined
  let ms: number | undefined
  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) {
    ms = seconds * 1000
  } else {
    const date = Date.parse(value)
    if (!Number.isNaN(date)) ms = Math.max(0, date - Date.now())
  }
  return ms !== undefined ? Math.min(ms, MAX_RETRY_DELAY_MS) : undefined
}

export class ProxyHttpClient {
  readonly endpoint: string
  private readonly apiKey: string
  private readonly defaultTimeoutMs: number
  private readonly authToken?: string
  private readonly maxRetries: number
  private readonly branch?: string
  private readonly site?: string
  private readonly organizationId?: string
  private readonly viewAsUserId?: string

  constructor(config: ProxyHttpClientConfig) {
    this.endpoint = normalizeEndpoint(config.endpoint)
    this.apiKey = config.apiKey
    this.defaultTimeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS
    this.authToken = config.authToken
    this.maxRetries = config.maxRetries ?? DEFAULT_MAX_RETRIES
    this.branch = config.branch
    this.site = config.site
    this.organizationId = config.organizationId
    this.viewAsUserId = config.viewAsUserId
  }

  /**
   * Return a client scoped to `branch` that stamps `X-Ontology-Branch` on
   * every outbound request (a per-call `options.headers` entry still wins).
   * Shares the parent's endpoint/auth/retry config. Returns `this` when
   * `branch` is empty or unchanged, so request handlers can forward the
   * active branch unconditionally without allocating a clone on the
   * baseline path.
   */
  withBranch(branch: string | undefined): ProxyHttpClient {
    if (!branch || branch === this.branch) return this
    return new ProxyHttpClient({
      endpoint: this.endpoint,
      apiKey: this.apiKey,
      timeoutMs: this.defaultTimeoutMs,
      authToken: this.authToken,
      maxRetries: this.maxRetries,
      branch,
      site: this.site,
      organizationId: this.organizationId,
      viewAsUserId: this.viewAsUserId
    })
  }

  /**
   * Return a client scoped to `organizationId` that stamps `X-Organization-ID` on
   * every outbound request (a per-call `options.headers` entry still wins).
   * Shares the parent's endpoint/auth/retry config. Returns `this` when
   * `organizationId` is empty or unchanged, so request handlers can forward the
   * active organization unconditionally without allocating a clone on the
   * baseline path.
   */
  withOrganization(organizationId: string | undefined): ProxyHttpClient {
    if (!organizationId || organizationId === this.organizationId) return this
    return new ProxyHttpClient({
      endpoint: this.endpoint,
      apiKey: this.apiKey,
      timeoutMs: this.defaultTimeoutMs,
      authToken: this.authToken,
      maxRetries: this.maxRetries,
      branch: this.branch,
      site: this.site,
      organizationId,
      viewAsUserId: this.viewAsUserId
    })
  }

  /**
   * Return a client scoped to `site` that stamps `X-Ontology-Site` on
   * every outbound request (a per-call `options.headers` entry still wins).
   * Shares the parent's endpoint/auth/retry config. Returns `this` when
   * `site` is empty or unchanged, matching `withBranch` behavior.
   */
  withSite(site: string | undefined): ProxyHttpClient {
    if (!site || site === this.site) return this
    return new ProxyHttpClient({
      endpoint: this.endpoint,
      apiKey: this.apiKey,
      timeoutMs: this.defaultTimeoutMs,
      authToken: this.authToken,
      maxRetries: this.maxRetries,
      branch: this.branch,
      organizationId: this.organizationId,
      site,
      viewAsUserId: this.viewAsUserId
    })
  }

  withToken(token: string | undefined): ProxyHttpClient {
    if (!token || token === this.authToken) return this
    return new ProxyHttpClient({
      endpoint: this.endpoint,
      apiKey: this.apiKey,
      timeoutMs: this.defaultTimeoutMs,
      maxRetries: this.maxRetries,
      branch: this.branch,
      site: this.site,
      organizationId: this.organizationId,
      viewAsUserId: this.viewAsUserId,
      authToken: token
    })
  }

  /**
   * Return a client scoped to a read-only user preview. `request` enforces
   * that this header can only be used on GET/HEAD calls.
   */
  withViewAsUser(userId: string | undefined): ProxyHttpClient {
    if (!userId || userId === this.viewAsUserId) return this
    return new ProxyHttpClient({
      endpoint: this.endpoint,
      apiKey: this.apiKey,
      timeoutMs: this.defaultTimeoutMs,
      authToken: this.authToken,
      maxRetries: this.maxRetries,
      branch: this.branch,
      site: this.site,
      organizationId: this.organizationId,
      viewAsUserId: userId
    })
  }

  async request<T = unknown>(
    method: string,
    path: string,
    options?: ProxyRequestOptions
  ): Promise<T> {
    const requestMethod = method.toUpperCase()
    if (this.viewAsUserId && requestMethod !== 'GET' && requestMethod !== 'HEAD') {
      throw new Error('X-Axilon-View-As-User may only be sent on GET or HEAD requests')
    }
    const url = `${this.endpoint}${path}`
    const maxAttempts = (options?.maxRetries ?? this.maxRetries) + 1

    const headers: Record<string, string> = {
      'X-API-Key': this.apiKey,
      ...options?.headers
    }
    // Branch-scoped clients stamp the active branch unless the caller
    // already set it explicitly via options.headers.
    if (this.branch) {
      headers[ONTOLOGY_BRANCH_HEADER] ??= this.branch
    }
    if (this.site) {
      headers[ONTOLOGY_SITE_HEADER] ??= this.site
    }
    if (this.organizationId) {
      headers['X-Organization-ID'] ??= this.organizationId
    }
    if (this.authToken) {
      headers['Authorization'] ??= `Bearer ${this.authToken}`
    }
    if (this.viewAsUserId) {
      headers[VIEW_AS_USER_HEADER] ??= this.viewAsUserId
    }
    const serializedBody = serializeRequestBody(options?.body, headers)
    const timeoutMs = options?.timeoutMs ?? this.defaultTimeoutMs
    const externalSignal = options?.signal

    if (externalSignal?.aborted) {
      throw makeAbortError(`Proxy ${method} ${path} aborted before send`)
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const isLastAttempt = attempt === maxAttempts - 1
      const timeoutController = new AbortController()
      const timeout = setTimeout(() => timeoutController.abort(), timeoutMs)

      // Combine the per-attempt timeout with the caller's external abort
      // so either source can tear down the fetch. The external signal is
      // distinguishable from the timeout post-hoc via `externalSignal.aborted`.
      const combinedSignal = externalSignal
        ? AbortSignal.any([timeoutController.signal, externalSignal])
        : timeoutController.signal

      let res: Response
      let responseText: string
      let receivedHeaders = false
      try {
        res = await fetch(url, {
          method,
          headers,
          signal: combinedSignal,
          body: serializedBody
        })
        receivedHeaders = true
        // Keep the deadline active until the whole response arrives. A lost
        // body is just as uncertain as a lost connection before the headers.
        responseText = await res.text()
      } catch (error) {
        // External cancel short-circuits the retry loop. A retried abort
        // would just abort again on the next attempt — and any in-flight
        // work the caller wants to stop should stop now.
        if (externalSignal?.aborted) {
          throw makeAbortError(`Proxy ${method} ${path} aborted by caller`)
        }
        const detail =
          isAbortError(error) || (error instanceof DOMException && error.name === 'AbortError')
            ? 'request timed out'
            : error instanceof Error
              ? error.message
              : String(error)

        // Receiving headers may mean the write committed. Preserve the
        // single-attempt body-read behavior; lifecycle callers reconcile it.
        if (isLastAttempt || receivedHeaders) {
          throw new ProxyTransportError(method, path, detail)
        }
        await sleep(retryDelay(attempt))
        if (externalSignal?.aborted) {
          throw makeAbortError(`Proxy ${method} ${path} aborted by caller`)
        }
        continue
      } finally {
        clearTimeout(timeout)
      }

      if (res.ok) {
        // 204 No Content (and other empty-body successes) have no JSON to
        // parse — `res.json()` throws `SyntaxError: Unexpected end of JSON
        // input` on those, which would otherwise mask a successful DELETE
        // as a generic 500 in the calling route. Return `undefined` so
        // `Promise<void>` callers (e.g. delete endpoints) resolve cleanly.
        if (res.status === 204 || res.headers.get('Content-Length') === '0') {
          return undefined as T
        }
        if (!responseText) return undefined as T
        return JSON.parse(responseText) as T
      }

      if (res.status === 401 || res.status === 403) {
        throw new ProxyHttpError(method, path, res.status, responseText, 'auth failed')
      }

      if (RETRYABLE_STATUS_CODES.has(res.status) && !isLastAttempt) {
        const retryAfterMs =
          res.status === 429 ? parseRetryAfter(res.headers.get('Retry-After')) : undefined
        await sleep(retryAfterMs ?? retryDelay(attempt))
        if (externalSignal?.aborted) {
          throw makeAbortError(`Proxy ${method} ${path} aborted by caller`)
        }
        continue
      }

      throw new ProxyHttpError(method, path, res.status, responseText)
    }

    throw new Error(`Proxy ${method} ${path} failed after ${maxAttempts} attempts`)
  }

  async get<T = unknown>(path: string, options?: Omit<ProxyRequestOptions, 'body'>): Promise<T> {
    return this.request<T>('GET', path, options)
  }

  async post<T = unknown>(
    path: string,
    body: unknown,
    options?: Omit<ProxyRequestOptions, 'body'>
  ): Promise<T> {
    return this.request<T>('POST', path, { ...options, body })
  }

  async put<T = unknown>(
    path: string,
    body: unknown,
    options?: Omit<ProxyRequestOptions, 'body'>
  ): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body })
  }

  async patch<T = unknown>(
    path: string,
    body: unknown,
    options?: Omit<ProxyRequestOptions, 'body'>
  ): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body })
  }

  async delete<T = unknown>(path: string, options?: Omit<ProxyRequestOptions, 'body'>): Promise<T> {
    return this.request<T>('DELETE', path, options)
  }
}
